// src/pages/Blog.js
import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import './Blog.css';

const Blog = () => {
  const { slug } = useParams(); // For detail view: /blog/:slug
  const [searchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category');

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPost, setCurrentPost] = useState(null);

  // Fetch all posts on mount
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setLoading(true);
        // ✅ Fixed: removed trailing spaces
        const response = await fetch('https://cms.vxpath.com/wp-json/wp/v2/posts?_embed&per_page=50');
        
        if (!response.ok) throw new Error('Failed to fetch blog posts');

        const data = await response.json();

        const formattedPosts = data.map(post => {
          const terms = post._embedded?.['wp:term']?.[0] || [];
          const categoryTerm = terms.find(term => term.taxonomy === 'category');
          const category = categoryTerm ? categoryTerm.name : 'General';
          
          const acf = post.acf || {};
          const full_content = acf.full_content || post.content?.rendered || '';
          const subtitle = acf.subtitle || '';
          const authorName = acf.author_name || '';
          const authorBio = acf.author_bio || '';

          let excerpt = post.excerpt.rendered
            .replace(/<[^>]+>/g, '')
            .trim()
            .replace(/\s+/g, ' ');
          if (!excerpt && post.content.rendered) {
            excerpt = post.content.rendered
              .replace(/<[^>]+>/g, '')
              .replace(/\s+/g, ' ')
              .substring(0, 160) + '...';
          }

          const featuredMedia = post._embedded?.['wp:featuredmedia']?.[0];
          const imageUrl = featuredMedia ? featuredMedia.source_url : null;

          return {
            id: post.id,
            title: post.title.rendered,
            full_content,     // ✅ ADDED
            subtitle,
            authorName,
            authorBio,
            excerpt,
            date: new Date(post.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }),
            category,
            slug: post.slug,
            imageUrl
          };
        });

        setPosts(formattedPosts);

        // If slug is in URL, find and set current post
        if (slug) {
          const found = formattedPosts.find(p => p.slug === slug);
          if (found) {
            setCurrentPost(found);
          } else {
            setError('Post not found');
          }
        }
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Unable to load blog posts.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, [slug]);

  // Filter posts by category
  const filteredPosts = selectedCategory
    ? posts.filter(post => post.category.toLowerCase() === selectedCategory.toLowerCase())
    : posts;

  if (loading) {
    return <div className="container mt-lg">Loading blog articles...</div>;
  }

  if (error) {
    return (
      <div className="container mt-lg">
        <p className="error">{error}</p>
        <Link to="/blog" className="btn-secondary">← Back to Blog</Link>
      </div>
    );
  }

  // === DETAIL VIEW ===
  if (slug && currentPost) {
    return (
      <div className="blog-page">
        <div className="container">
          <nav className="breadcrumb-nav" aria-label="Breadcrumb">
            <Link to="/blog">← Back to Articles</Link>
          </nav>

          <article className="blog-post-detail">
            {currentPost.imageUrl && (
              <img 
                src={currentPost.imageUrl} 
                alt={currentPost.title} 
                className="blog-post-image" 
              />
            )}
            
            <div className="blog-meta">
              <span className="blog-date">{currentPost.date}</span>
              <span className="blog-category">{currentPost.category}</span>
            </div>

            <h1>{currentPost.title}</h1>

            {currentPost.subtitle && (
              <p className="blog-subtitle">{currentPost.subtitle}</p>
            )}

            {currentPost.authorName && (
              <p className="blog-author">By {currentPost.authorName}</p>
            )}

            {/* ✅ RENDER FULL CONTENT */}
            <div 
              className="blog-post-content"
              dangerouslySetInnerHTML={{ __html: currentPost.full_content }}
            />

            {currentPost.authorBio && (
              <blockquote className="blog-author-bio">"{currentPost.authorBio}"</blockquote>
            )}

            <div className="blog-post-actions">
              <Link to="/contact" className="btn-primary">Contact Us</Link>
              <Link to="/blog" className="btn-secondary">← All Articles</Link>
            </div>
          </article>
        </div>
      </div>
    );
  }

  // === LIST VIEW ===
  return (
    <div className="blog-page">
      <div className="container">
        <div className="page-header">
          <h1>Insights & Articles</h1>
          <p>Expert perspectives on technology, security, and digital transformation</p>
        </div>

        {selectedCategory && (
          <div className="mb-md">
            <Link to="/blog" className="btn-secondary btn-sm">
              ← Clear filter: {selectedCategory}
            </Link>
          </div>
        )}

        <div className="blog-layout">
          <div className="blog-main">
            {filteredPosts.length > 0 ? (
              filteredPosts.map(post => (
                <article key={post.id} className="blog-card">
                  {post.imageUrl && (
                    <img 
                      src={post.imageUrl} 
                      alt={post.title} 
                      className="blog-card-image" 
                    />
                  )}
                  <div className="blog-meta">
                    <span className="blog-date">{post.date}</span>
                    <span className="blog-category">{post.category}</span>
                  </div>
                  <h2>
                    <Link to={`/blog/${post.slug}`} className="blog-title-link">
                      {post.title}
                    </Link>
                  </h2>
                  {post.subtitle && <p className="blog-subtitle">{post.subtitle}</p>}
                  {post.authorName && <p className="blog-author">{post.authorName}</p>}
                  <p className="blog-excerpt">{post.excerpt}</p>
                  <Link to={`/blog/${post.slug}`} className="blog-read-more">
                    Read full article →
                  </Link>
                </article>
              ))
            ) : (
              <p>No articles found in category "{selectedCategory}".</p>
            )}
          </div>

          <aside className="blog-sidebar">
            <div className="sidebar-card">
              <h3>Latest Articles</h3>
              <ul>
                {posts.slice(0, 4).map(post => (
                  <li key={post.id}>
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="sidebar-card">
              <h3>Categories</h3>
              <ul>
                <li>
                  <Link 
                    to="/blog" 
                    className={!selectedCategory ? "active-filter" : ""}
                  >
                    All
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/blog?category=security"
                    className={selectedCategory === 'security' ? "active-filter" : ""}
                  >
                    Security
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/blog?category=automation"
                    className={selectedCategory === 'automation' ? "active-filter" : ""}
                  >
                    Automation
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/blog?category=network"
                    className={selectedCategory === 'network' ? "active-filter" : ""}
                  >
                    Network
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/blog?category=cloud"
                    className={selectedCategory === 'cloud' ? "active-filter" : ""}
                  >
                    Cloud
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Blog;